import type React from 'react';

import type { AdFields } from '@xing-com/crate-ads-native-ads';
import { ApplyButtonStateProvider } from '@xing-com/crate-jobs-components-apply-button';
import type { JobDetails, SimilarJobData } from '@xing-com/crate-jobs-helpers';
import { useIsJobDetailHeaderInView } from '@xing-com/crate-jobs-hooks';
import { NoSSR } from '@xing-com/crate-utils-no-ssr';
import { useExperiment } from '@xing-com/hub';

import { JobDetailModule } from '../job-details/job-detail-module';
import { SimilarJobsModule } from '../similar-jobs-module';

import { MainActions, TopActions } from './actions';
import {
  StyledConfirmation,
  StyledHeader,
  StyledSimilarJobsWrapper,
  TopContent,
  TopContentLeft,
  TopContentRight,
} from './header.styles';
import { EarnUpToHighlight, Highlights } from './highlights';
import { JobIntro } from './job-intro';
import { PublishedDate } from './published-date';
import { RequirementsBanner } from './requirements-banner';
import { StickyHeader } from './sticky-header';

type Props = {
  isSimilarJobsLoading?: boolean;
  isSimilarJobsVisible?: boolean;
  job: JobDetails;
  similarJobs?: SimilarJobData[];
  ads: AdFields[];
  isLoadingAds: boolean;
};

export const Header: React.FC<Props> = ({
  isSimilarJobsLoading = false,
  isSimilarJobsVisible = false,
  job,
  similarJobs = [],
  ads = [],
  isLoadingAds = false,
}) => {
  const {
    ref: stickyHeaderRef,
    refElement: stickyHeaderRefElement,
    isInView,
  } = useIsJobDetailHeaderInView();

  const isRequirementsBannerEnabled = useExperiment('ABACUS-551') === 'B';

  const {
    refreshedAt,
    application,
    bookmarkState,
    company,
    facts,
    highlights,
    id,
    industry,
    isReachRefreshed,
    locations,
    salary,
    screenOutCriteria,
    slug,
    title,
  } = job;

  const withStickyHeader = !job.redirectsToThirdPartyUrl && !isInView;
  const stickyHeaderProps = {
    job,
    show: withStickyHeader,
    headerRef: stickyHeaderRefElement,
  };

  return (
    <ApplyButtonStateProvider
      jobSlug={slug}
      jobId={id}
      applicationType={application.type}
    >
      <StyledHeader ref={stickyHeaderRef} data-testid="job-details-header">
        {isSimilarJobsVisible ? (
          <StyledSimilarJobsWrapper>
            <JobDetailModule visible={isSimilarJobsVisible}>
              <SimilarJobsModule
                isLoading={isSimilarJobsLoading}
                similarJobs={similarJobs}
                showCarousel
                ads={ads}
                isLoadingAds={isLoadingAds}
                titleSize="medium"
                titleSizeConfined="large"
              />
            </JobDetailModule>
          </StyledSimilarJobsWrapper>
        ) : null}
        <TopContent>
          <TopContentLeft>
            <PublishedDate date={refreshedAt} />
          </TopContentLeft>
          <TopContentRight>
            <EarnUpToHighlight label={highlights.earnUpTo} />
            <TopActions
              applicationStatus={application.status}
              bookmarkState={bookmarkState}
              isHeaderSticky={withStickyHeader}
              job={job}
            />
          </TopContentRight>
        </TopContent>
        <JobIntro
          title={title}
          company={company}
          industry={industry?.name}
          isReachRefreshed={isReachRefreshed}
          locations={locations}
        />
        <Highlights highlights={highlights} facts={facts} salary={salary} />
        <NoSSR>
          {/*
            The confirmation banner looks into local storage to know if a
            LO user has applied to the current job. The NoSSR component is used
            to ensure that it is rendered only on client side.
          */}
          <StyledConfirmation application={application} />
        </NoSSR>
        <MainActions
          application={application}
          job={job}
          bookmarkState={bookmarkState}
        />
        {isRequirementsBannerEnabled ? (
          <NoSSR>
            <RequirementsBanner
              application={application}
              {...screenOutCriteria}
            />
          </NoSSR>
        ) : null}
      </StyledHeader>
      <StickyHeader {...stickyHeaderProps} />
    </ApplyButtonStateProvider>
  );
};
